import React, { useCallback, useState, useContext } from 'react';
import { Upload, File, X, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';
import { AuthContext } from '../../contexts/AuthContext';

const FileUploadZone = ({ onFileSelect, onUpload, uploading, uploadError, selectedLibrary, disabled = false }) => {
  const { user } = useContext(AuthContext);
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled) return;
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, [disabled]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (disabled) return;
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  }, [disabled]);

  const handleFileSelect = (file) => {
    // Validate file type
    const allowedTypes = [
      'application/json',
      'text/csv', 
      'application/csv',
      'text/xml',
      'application/xml',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    const allowedExtensions = ['.json', '.csv', '.xml', '.xls', '.xlsx'];
    const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
      alert('Please select a valid file type: JSON, CSV, XML, or Excel');
      return;
    }

    setSelectedFile(file);
    onFileSelect(file);
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0]);
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    onFileSelect(null);
  };

  const handleUpload = () => {
    if (selectedFile && selectedLibrary) {
      onUpload(selectedFile);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600 mb-4">
        <p className="mb-2">
          <strong>Supported formats:</strong> CDISC ODM XML (regulatory-compliant) or CSV
        </p>
        <p className="text-xs">
          Upload CDISC-compliant assessment files. The system will automatically detect the file format and ODM version.
        </p>
      </div>

      {/* File Drop Zone */}
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-8 text-center transition-all duration-200
          ${dragActive 
            ? 'border-primary-400 bg-primary-50' 
            : selectedFile 
              ? 'border-green-400 bg-green-50' 
              : 'border-gray-300 bg-gray-50 dark:bg-gray-900 hover:border-gray-400'
          }
          ${uploading || disabled ? 'opacity-50 pointer-events-none cursor-not-allowed' : 'cursor-pointer'}
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={() => !uploading && !disabled && document.getElementById('file-input').click()}
      >
        <input
          id="file-input"
          type="file"
          className="hidden"
          accept=".json,.csv,.xml,.xls,.xlsx"
          onChange={handleFileInputChange}
          disabled={uploading || disabled}
        />
        
        {!selectedFile ? (
          <div className="space-y-4">
            <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${
              dragActive ? 'bg-primary-100' : 'bg-gray-100'
            }`}>
              <Upload className={`w-8 h-8 ${dragActive ? 'text-primary-600' : 'text-gray-400'}`} />
            </div>
            <div>
              <p className="text-lg font-medium text-gray-900 mb-2">
                {dragActive ? 'Drop your file here' : 'Upload CDISC Assessment File'}
              </p>
              <p className="text-sm text-gray-600">
                Drag and drop your file here, or <span className="text-primary-600 font-medium">browse</span>
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-center space-x-3">
              <File className="w-8 h-8 text-green-600" />
              <div className="text-left">
                <p className="font-medium text-gray-900">{selectedFile.name}</p>
                <p className="text-sm text-gray-600">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              {!uploading && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile();
                  }}
                  className="text-gray-400 hover:text-red-500 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        )}

        {uploading && (
          <div className="absolute inset-0 bg-white bg-opacity-75 dark:bg-gray-800 dark:bg-opacity-75 flex items-center justify-center rounded-lg">
            <div className="flex items-center space-x-3">
              <Loader2 className="w-6 h-6 text-primary-600 animate-spin" />
              <span className="text-sm font-medium text-gray-700">Uploading and processing...</span>
            </div>
          </div>
        )}
      </div>

      {/* Upload Error */}
      {uploadError && (
        <div className="flex items-start space-x-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-red-800">Upload Failed</h4>
            <p className="text-sm text-red-700 mt-1">{uploadError}</p>
          </div>
        </div>
      )}

      {/* Library Warning */}
      {!selectedLibrary && (
        <div className="flex items-start space-x-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="text-sm font-medium text-amber-800">Select Library First</h4>
            <p className="text-sm text-amber-700 mt-1">
              Please choose a library location above before uploading your file.
            </p>
          </div>
        </div>
      )}

      {/* Upload Button */}
      {selectedFile && selectedLibrary && !uploading && (
        <div className="flex justify-end">
          <button
            onClick={handleUpload}
            disabled={disabled}
            className={`
              inline-flex items-center px-4 py-2 text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500
              ${disabled 
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
                : 'bg-primary-600 text-white hover:bg-primary-700'
              }
            `}
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload & Process Assessment
          </button>
        </div>
      )}
    </div>
  );
};

export default FileUploadZone;
